import { zodResolver } from '@hookform/resolvers/zod'
import { type PostApiKeysData, postApiKeys } from '@repo/api-portal-sdk/gen'
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/alert'
import { Button } from '@repo/ui/button'
import {
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@repo/ui/dialog'
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel
} from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import { Spinner } from '@repo/ui/spinner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, CircleAlert, Copy, ShieldAlert } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/lib/query-keys'

const API_KEY_LABEL_MAX_LENGTH = 128

export function ApiKeyCreationDialogContent() {
	return (
		<DialogContent showCloseButton={false}>
			<ApiKeyCreationDialogBody />
		</DialogContent>
	)
}

function ApiKeyCreationDialogBody() {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const [createdSecret, setCreatedSecret] = useState<{
		key: string
		label: string
	} | null>(null)
	const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>(
		'idle'
	)
	const schema = createApiKeySchema({
		required: t('common:dashboard.key_name_required'),
		tooLong: t('common:dashboard.key_name_too_long')
	})
	const form = useForm<z.infer<ReturnType<typeof createApiKeySchema>>>({
		defaultValues: { label: '' },
		resolver: zodResolver(schema)
	})
	const createMutation = useMutation({
		mutationFn: (body: PostApiKeysData['body']) =>
			postApiKeys({ body }).then((response) => response.data.data),
		onSuccess: async (createdKey) => {
			const { key, ...apiKey } = createdKey
			setCreatedSecret({ key, label: apiKey.label })
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.apiKeys.all()
				}),
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.billing.usage()
				})
			])
		}
	})

	async function copySecret() {
		if (!createdSecret) {
			return
		}

		try {
			await navigator.clipboard.writeText(createdSecret.key)
			setCopyState('copied')
		} catch {
			setCopyState('failed')
		}
	}

	if (createdSecret) {
		return (
			<>
				<DialogHeader>
					<DialogTitle>
						{t('common:dashboard.key_created_title')}
					</DialogTitle>
					<DialogDescription>
						{t('common:dashboard.key_created_description', {
							label: createdSecret.label
						})}
					</DialogDescription>
				</DialogHeader>
				<div className='api-key-secret flex flex-col items-stretch gap-3'>
					<code className='min-w-0 flex-1'>{createdSecret.key}</code>
					<Button
						className='self-end'
						onClick={copySecret}
						type='button'
						variant='outline'
					>
						{copyState === 'copied' ? (
							<Check aria-hidden='true' />
						) : (
							<Copy aria-hidden='true' />
						)}
						<span aria-live='polite'>
							{t(
								copyState === 'copied'
									? 'common:dashboard.key_copied'
									: 'common:dashboard.copy_key'
							)}
						</span>
					</Button>
				</div>
				{copyState === 'failed' ? (
					<p className='text-destructive text-sm' role='alert'>
						{t('common:dashboard.key_copy_failed')}
					</p>
				) : null}
				<Alert>
					<ShieldAlert aria-hidden='true' />
					<AlertTitle>
						{t('common:dashboard.store_key_title')}
					</AlertTitle>
					<AlertDescription>
						{t('common:dashboard.new_key_description')}
					</AlertDescription>
				</Alert>
				<DialogFooter>
					<DialogClose render={<Button type='button' />}>
						{t('common:dashboard.key_saved')}
					</DialogClose>
				</DialogFooter>
			</>
		)
	}

	return (
		<>
			<DialogHeader>
				<DialogTitle>
					{t('common:dashboard.create_key_title')}
				</DialogTitle>
				<DialogDescription>
					{t('common:dashboard.create_key_description')}
				</DialogDescription>
			</DialogHeader>
			<form
				className='grid gap-6'
				onSubmit={form.handleSubmit((data) =>
					createMutation.mutate({ data: { label: data.label } })
				)}
			>
				<FieldGroup className='gap-4'>
					<Controller
						control={form.control}
						name='label'
						render={({ field, fieldState }) => (
							<Field
								className='gap-1.5'
								data-invalid={fieldState.invalid}
							>
								<FieldLabel htmlFor={field.name}>
									{t('common:dashboard.key_name_label')}
								</FieldLabel>
								<Input
									{...field}
									aria-describedby={`${field.name}-description ${field.name}-error`}
									aria-invalid={fieldState.invalid}
									autoComplete='off'
									id={field.name}
									placeholder={t(
										'common:dashboard.key_name_placeholder'
									)}
								/>
								{fieldState.error ? (
									<FieldError
										errors={[fieldState.error]}
										id={`${field.name}-error`}
									/>
								) : null}
								<FieldDescription
									id={`${field.name}-description`}
								>
									{t('common:dashboard.key_name_description')}
								</FieldDescription>
							</Field>
						)}
					/>
					{createMutation.isError ? (
						<Alert variant='destructive'>
							<CircleAlert aria-hidden='true' />
							<AlertTitle>
								{t('common:dashboard.key_create_failed')}
							</AlertTitle>
							<AlertDescription>
								{t(
									'common:dashboard.key_create_failed_description'
								)}
							</AlertDescription>
						</Alert>
					) : null}
				</FieldGroup>
				<DialogFooter>
					<DialogClose
						render={<Button type='button' variant='outline' />}
					>
						{t('common:dashboard.cancel')}
					</DialogClose>
					<Button disabled={createMutation.isPending} type='submit'>
						{createMutation.isPending ? <Spinner /> : null}
						{t(
							createMutation.isPending
								? 'common:dashboard.creating_key'
								: 'common:dashboard.create_key'
						)}
					</Button>
				</DialogFooter>
			</form>
		</>
	)
}

function createApiKeySchema(messages: { required: string; tooLong: string }) {
	return z.object({
		label: z
			.string()
			.trim()
			.min(1, messages.required)
			.max(API_KEY_LABEL_MAX_LENGTH, messages.tooLong)
	})
}
